import { create } from 'zustand';
import type { AdminInvoiceDto, InvoiceKind, InvoiceListQuery, InvoiceSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { saveFile } from '@/lib/download';

/**
 * The invoice register (ADR-067): every invoice and credit note the store has
 * issued, searched and summed on the server — the list the accountant asks for.
 */

export interface InvoiceFilters {
  search: string;
  kind: '' | InvoiceKind;
  /** The store's days, `YYYY-MM-DD`. */
  from: string;
  to: string;
}

export const EMPTY_INVOICE_FILTERS: InvoiceFilters = { search: '', kind: '', from: '', to: '' };

const PAGE_SIZE = 20;

interface InvoicesState {
  items: AdminInvoiceDto[];
  cursor: string | null;
  total: number | null;
  summary: InvoiceSummaryDto | null;
  filters: InvoiceFilters;
  loading: boolean;
  exporting: boolean;
  /** The document being fetched, so its row can say so. */
  opening: string | null;
  error: string | null;

  load: (patch?: Partial<InvoiceFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  exportCsv: () => Promise<void>;
  open: (document: Pick<AdminInvoiceDto, 'id' | 'number'>) => Promise<void>;
}

function toQuery(filters: InvoiceFilters): Omit<InvoiceListQuery, 'limit' | 'cursor'> {
  return {
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };
}

export const useInvoicesStore = create<InvoicesState>((set, get) => ({
  items: [],
  cursor: null,
  total: null,
  summary: null,
  filters: EMPTY_INVOICE_FILTERS,
  loading: false,
  exporting: false,
  opening: null,
  error: null,

  async load(patch) {
    const filters = { ...get().filters, ...patch };
    set({ filters, loading: true, error: null });
    try {
      const query = toQuery(filters);
      const [page, summary] = await Promise.all([
        withSession(() => adminApi.invoices.list({ ...query, limit: PAGE_SIZE })),
        withSession(() => adminApi.invoices.summary({ search: query.search, from: query.from, to: query.to })),
      ]);
      // A slower answer to an older filter must not overwrite a newer one.
      if (get().filters !== filters) return;
      set({
        items: page.items,
        cursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        summary,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the invoices.') });
    }
  },

  async loadMore() {
    const { cursor, filters, loading } = get();
    if (!cursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() => adminApi.invoices.list({ ...toQuery(filters), limit: PAGE_SIZE, cursor }));
      set((state) => ({ items: [...state.items, ...page.items], cursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more invoices.') });
    }
  },

  async exportCsv() {
    const { filters, exporting } = get();
    if (exporting) return;
    set({ exporting: true, error: null });
    try {
      // The whole filter, not a page of it: the server builds the file.
      await saveFile(() => withSession(() => adminApi.invoices.export(toQuery(filters))), 'invoices.csv');
    } catch (error) {
      set({ error: errorMessage(error, 'Could not export the invoices.') });
    } finally {
      set({ exporting: false });
    }
  },

  async open(document) {
    if (get().opening) return;
    set({ opening: document.id, error: null });
    try {
      await saveFile(() => withSession(() => adminApi.invoices.document(document.id)), `${document.number}.pdf`);
    } catch (error) {
      set({ error: errorMessage(error, 'Could not open the document.') });
    } finally {
      set({ opening: null });
    }
  },
}));
